const positionToString = (position) => {
    return `${position.column}_${position.row}`;
};
const arePositionsSame = (pos1, pos2) => {
    return pos1.column === pos2.column && pos1.row === pos2.row;
};
const collectPath = (cameFrom, targetPosition) => {
    const path = [targetPosition];
    let currentId = positionToString(targetPosition);
    while (cameFrom[currentId]) {
        path.push(cameFrom[currentId]);
        currentId = positionToString(cameFrom[currentId]);
    }
    return path.reverse();
};
const decoratePathWithCost = (path, costs) => {
    return path.map((position) => {
        return {
            ...position,
            cost: costs[positionToString(position)],
        };
    });
};
export const findPathBfs = ({ startPosition, targetPosition, getExistingNeighbours, }) => {
    const frontier = [];
    frontier.push(startPosition);
    const cameFrom = {};
    cameFrom[positionToString(startPosition)] = null;
    while (frontier.length > 0) {
        const current = frontier.shift();
        if (!current) {
            continue;
        }
        if (arePositionsSame(current, targetPosition)) {
            break;
        }
        const neighbours = getExistingNeighbours(current);
        neighbours.forEach((neighbour) => {
            const stringId = positionToString(neighbour);
            if (cameFrom[stringId] === undefined) {
                frontier.push(neighbour);
                cameFrom[stringId] = current;
            }
        });
    }
    return collectPath(cameFrom, targetPosition);
};
const insertIntoPriorityQueue = (queue, position) => {
    const idx = queue.findIndex((p) => p.priority >= position.priority);
    if (idx < 0) {
        queue.push(position);
    }
    else {
        queue.splice(idx, 0, position);
    }
};
export const findPathDijkstra = ({ startPosition, targetPosition, getExistingNeighbours, getMovementCost, }) => {
    // frontier is a priority queue (an array sorted by cost)
    const frontier = [];
    frontier.push({ ...startPosition, priority: 0 });
    const startPositionId = positionToString(startPosition);
    const cameFrom = {};
    cameFrom[startPositionId] = null;
    const costSoFar = {};
    costSoFar[startPositionId] = 0;
    while (frontier.length > 0) {
        const current = frontier.shift();
        if (!current) {
            continue;
        }
        const currentId = positionToString(current);
        const neighbours = getExistingNeighbours(current);
        neighbours.forEach((next) => {
            const nextId = positionToString(next);
            const newCost = costSoFar[currentId] + getMovementCost(current, next);
            if (costSoFar[nextId] === undefined || newCost < costSoFar[nextId]) {
                insertIntoPriorityQueue(frontier, { ...next, priority: newCost });
                cameFrom[nextId] = current;
                costSoFar[nextId] = newCost;
            }
        });
    }
    return collectPath(cameFrom, targetPosition);
};
export const findPathAStar = ({ startPosition, targetPosition, getExistingNeighbours, getMovementCost, getHeuristicDistance, }) => {
    // frontier is a priority queue (an array sorted by cost)
    const frontier = [];
    frontier.push({ ...startPosition, priority: 0 });
    const startPositionId = positionToString(startPosition);
    const targetPositionId = positionToString(targetPosition);
    const cameFrom = {};
    cameFrom[startPositionId] = null;
    const costSoFar = {};
    costSoFar[startPositionId] = 0;
    while (frontier.length > 0) {
        const current = frontier.shift();
        if (!current) {
            continue;
        }
        if (arePositionsSame(current, targetPosition)) {
            break;
        }
        const currentId = positionToString(current);
        const neighbours = getExistingNeighbours(current);
        neighbours.forEach((next) => {
            const nextId = positionToString(next);
            const newCost = costSoFar[currentId] + getMovementCost(current, next);
            if (costSoFar[nextId] === undefined || newCost < costSoFar[nextId]) {
                insertIntoPriorityQueue(frontier, {
                    ...next,
                    priority: newCost + getHeuristicDistance(next, targetPosition),
                });
                cameFrom[nextId] = current;
                costSoFar[nextId] = newCost;
            }
        });
    }
    const path = collectPath(cameFrom, targetPosition);
    return {
        path,
        cost: costSoFar[targetPositionId],
        pathWithCost: decoratePathWithCost(path, costSoFar),
    };
};
